import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { path: "/", label: "Startseite" },
  { path: "/ueber-uns", label: "Über uns" },
  { path: "/leistungen", label: "Leistungen" },
  { path: "/qualitaet", label: "Qualität & Umwelt" },
  { path: "/kontakt", label: "Kontakt" },
];

function Logo() {
  return (
    <Link to="/" className="flex flex-col items-start select-none z-50 relative">
      <span className="font-heading font-bold text-foreground-950 tracking-wide leading-tight" style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1.1rem)' }}>
        DRAUDE & WAGNER
      </span>
      <span className="font-heading font-semibold tracking-widest text-primary-500 leading-tight" style={{ fontSize: 'clamp(0.65rem, 2vw, 0.85rem)' }}>
        GALVANIK
      </span>
    </Link>
  );
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200/70 transition-shadow duration-300"
      style={scrolled ? { boxShadow: '0 1px 3px rgba(0,0,0,0.05)' } : undefined}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === item.path
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile burger button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden relative w-11 h-11 flex flex-col items-center justify-center gap-1.5 rounded-md transition-colors hover:bg-background-100 z-50"
            aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isOpen}
          >
            <span
              className={`block w-6 h-0.5 bg-foreground-900 transition-transform duration-300 ${
                isOpen ? "rotate-45 translate-y-[4px]" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-foreground-900 transition-opacity duration-300 ${
                isOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-foreground-900 transition-transform duration-300 ${
                isOpen ? "-rotate-45 -translate-y-[4px]" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background-50/98 backdrop-blur-md z-40 overflow-y-auto">
          <nav className="flex flex-col items-center gap-2 pt-8 pb-12 px-6">
            {/* Logo repeated in mobile menu for visibility */}
            <div className="mb-6 bg-white rounded-lg px-6 py-4 border border-background-200">
              <Logo />
            </div>
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`w-full max-w-sm text-center py-4 px-6 rounded-lg text-lg font-medium bg-white border border-background-200 transition-colors ${
                  location.pathname === item.path
                    ? "text-primary-600"
                    : "text-foreground-950 hover:text-primary-600"
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}